import { notificationService } from '../services/notificationService'
import type { PushSubscriptionPayload } from '../types/notification'

export type PushStatus =
  | 'unsupported'
  | 'denied'
  | 'subscribed'
  | 'unsubscribed'

const UNSUPPORTED_MESSAGE =
  "Browser notifications aren't available in this browser."

export const isPushSupported = (): boolean =>
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

// Converts a base64url-encoded VAPID public key into the Uint8Array
// format required by pushManager.subscribe()
export const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(normalized)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const isProduction = process.env.NODE_ENV === 'production'

const SERVICE_WORKER_TIMEOUT_MS = 15000

// Note: we deliberately avoid navigator.serviceWorker.ready here — in
// development the service worker is never registered, so `ready` would
// never resolve. getRegistration() resolves with undefined instead.
const getRegistration = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  if (!isPushSupported()) {
    return undefined
  }
  return navigator.serviceWorker.getRegistration()
}

// The app's ConditionalServiceWorker keeps the worker registered only on
// /tags, so on other pages (like Profile) there is usually no
// registration. Register one on demand. serviceWorkerRegistration's
// register() can't be used here: it waits for the window `load` event,
// which never re-fires after SPA navigation, so register the worker URL
// directly.
const ensureRegistration = async (): Promise<ServiceWorkerRegistration> => {
  const existing = await getRegistration()
  if (existing) {
    return existing
  }
  if (!isProduction) {
    throw new Error(UNSUPPORTED_MESSAGE)
  }
  await navigator.serviceWorker.register(
    `${process.env.PUBLIC_URL}/service-worker.js`,
  )
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () =>
        reject(
          new Error(
            'The notification service worker took too long to start. Please try again.',
          ),
        ),
      SERVICE_WORKER_TIMEOUT_MS,
    ),
  )
  return Promise.race([navigator.serviceWorker.ready, timeout])
}

export const getPushStatus = async (): Promise<PushStatus> => {
  if (!isPushSupported()) {
    return 'unsupported'
  }

  if (Notification.permission === 'denied') {
    return 'denied'
  }

  const registration = await getRegistration()
  if (!registration) {
    // No worker on this page yet — in production enablePush() registers
    // one on demand; in development registration never happens at all.
    return isProduction ? 'unsubscribed' : 'unsupported'
  }

  const subscription = await registration.pushManager.getSubscription()
  return subscription ? 'subscribed' : 'unsubscribed'
}

export const enablePush = async (): Promise<void> => {
  const registration = await ensureRegistration()

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(
      'Notification permission was not granted. Please allow notifications in your browser settings.',
    )
  }

  const { public_key: publicKey } = await notificationService.getPushPublicKey()
  if (!publicKey) {
    throw new Error('Push notifications are not configured')
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  // If anything past this point fails, drop the just-created browser
  // subscription — otherwise getPushStatus() would report 'subscribed'
  // while the backend has no endpoint and no pushes ever arrive.
  try {
    const { endpoint, keys } = subscription.toJSON()
    if (!endpoint || !keys?.p256dh || !keys?.auth) {
      throw new Error('Failed to create a push subscription')
    }

    const payload: PushSubscriptionPayload = {
      endpoint,
      keys: {
        p256dh: keys.p256dh,
        auth: keys.auth,
      },
      user_agent: navigator.userAgent.slice(0, 255),
    }
    await notificationService.subscribeToPush(payload)
  } catch (error) {
    await subscription.unsubscribe().catch(() => {
      // best effort — the retry path resubscribes with the same endpoint
    })
    throw error
  }
}

export const disablePush = async (): Promise<void> => {
  const registration = await getRegistration()
  if (!registration) {
    return
  }

  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    return
  }

  await notificationService.unsubscribeFromPush(subscription.endpoint)
  await subscription.unsubscribe()
}
